import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Avatar,
  FormControl,
  FormLabel,
  Input,
  Card,
  CardBody,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../lib/firebase';
import { sendPasswordResetEmail, updateProfile, getAuth } from 'firebase/auth';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User } from '../types/user';

const ProfilePage: React.FC = () => {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(user?.displayName || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.photoURL || '');
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [avatarBgColor, setAvatarBgColor] = useState(user?.avatarBgColor || 'blue.500');
  const bgColor = useColorModeValue('white', 'gray.700');
  const linkBgColor = useColorModeValue('gray.50', 'gray.700');
  const avatarColor = useColorModeValue('white', 'gray.800');

  const colorOptions = [
    { name: 'Azul', value: 'blue.500' },
    { name: 'Verde', value: 'green.500' },
    { name: 'Rojo', value: 'red.500' },
    { name: 'Morado', value: 'purple.500' },
    { name: 'Naranja', value: 'orange.500' },
    { name: 'Rosa', value: 'pink.500' },
  ];

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    
    try {
      setLoading(true);
      const auth = getAuth();
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true
      };
      
      await sendPasswordResetEmail(auth, user.email, actionCodeSettings);
      
      // Obtener el enlace de restablecimiento
      const resetLink = `https://${auth.config.authDomain}/__/auth/action?mode=resetPassword&oobCode=`;
      setResetLink(resetLink);
      
      toast({
        title: 'Enlace generado',
        description: 'Se ha generado el enlace para restablecer tu contraseña',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error al generar enlace:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el enlace de restablecimiento',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Actualizar photoURL en Firebase Auth
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: avatarUrl || null
        });
      }

      // Verificar si el documento existe y actualizarlo o crearlo
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      const userData = {
        email: user.email,
        updated_at: new Date(),
        photoURL: avatarUrl || null,
        avatarBgColor: avatarBgColor
      };

      if (userDoc.exists()) {
        // Si el documento existe, actualizarlo
        await updateDoc(userRef, userData);
      } else {
        // Si el documento no existe, crearlo
        await setDoc(userRef, {
          ...userData,
          uid: user.uid,
          name: user.name,
          displayName: user.displayName,
          createdAt: new Date()
        });
      }

      // Actualizar el estado del usuario en el contexto
      const updatedUser = {
        ...user,
        photoURL: avatarUrl || null,
        avatarBgColor: avatarBgColor
      };
      setUser(updatedUser);

      toast({
        title: 'Perfil actualizado',
        description: 'Tu perfil se ha actualizado correctamente',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el perfil',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Eliminar photoURL en Firebase Auth
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: null
        });
      }

      // Actualizar en Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        photoURL: null,
        updated_at: new Date()
      });

      // Actualizar el estado del usuario en el contexto
      const updatedUser = {
        ...user,
        photoURL: null
      };
      setUser(updatedUser);

      // Limpiar el estado local
      setAvatarUrl('');

      // Forzar la actualización del perfil en Firebase Auth
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: null
        });
      }

      toast({
        title: 'Avatar eliminado',
        description: 'Tu foto de perfil ha sido eliminada',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error al eliminar avatar:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la foto de perfil',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Debes iniciar sesión para ver tu perfil
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg">Mi Perfil</Heading>

        <Card bg={bgColor}>
          <CardBody>
            <VStack spacing={6}>
              <Avatar
                size="2xl"
                name={user.name || user.displayName || user.email || 'Usuario'}
                src={avatarUrl || user.photoURL || undefined}
                bg={avatarUrl || user.photoURL ? undefined : avatarBgColor}
                color={avatarColor}
              />

              <FormControl>
                <FormLabel>Nombre de usuario</FormLabel>
                <Text>{user.name || user.displayName || user.email || 'Usuario'}</Text>
              </FormControl>

              <FormControl>
                <FormLabel>URL del avatar (opcional)</FormLabel>
                <HStack>
                  <Input
                    value={avatarUrl}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                    placeholder="https://ejemplo.com/avatar.jpg"
                  />
                  {(avatarUrl || user.photoURL) && (
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={handleRemoveAvatar}
                      isLoading={loading}
                    >
                      Eliminar
                    </Button>
                  )}
                </HStack>
                {avatarUrl && (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    La imagen se actualizará cuando guardes los cambios
                  </Text>
                )}
              </FormControl>

              {!avatarUrl && !user.photoURL && (
                <FormControl>
                  <FormLabel>Color de fondo del avatar</FormLabel>
                  <HStack wrap="wrap" spacing={2}>
                    {colorOptions.map((color) => (
                      <Button
                        key={color.value}
                        size="sm"
                        bg={color.value}
                        color="white"
                        _hover={{ opacity: 0.8 }}
                        onClick={() => setAvatarBgColor(color.value)}
                        borderWidth={avatarBgColor === color.value ? 2 : 0}
                        borderColor="black"
                      >
                        {color.name}
                      </Button>
                    ))}
                  </HStack>
                </FormControl>
              )}

              <Button
                colorScheme="blue"
                onClick={handleUpdateProfile}
                isLoading={loading}
                width="full"
              >
                Actualizar Perfil
              </Button>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={bgColor}>
          <CardBody>
            <VStack spacing={4}>
              <Heading size="md">Seguridad</Heading>
              <Text>
                Email: {user.email}
              </Text>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={handlePasswordReset}
                isLoading={loading}
              >
                Generar Enlace de Para Restablecer la Contraseña
              </Button>
              
              {resetLink && (
                <Box width="full" p={4} borderWidth={1} borderRadius="md">
                  <Text fontWeight="bold" mb={2}>Enlace Para Restablecer la Contraseña:</Text>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Copia este enlace y pégalo en tu navegador para restablecer tu contraseña:
                  </Text>
                  <Text
                    p={2}
                    bg={linkBgColor}
                    borderRadius="md"
                    fontSize="sm"
                    wordBreak="break-all"
                  >
                    {resetLink}
                  </Text>
                  <Button
                    size="sm"
                    mt={2}
                    onClick={() => {
                      navigator.clipboard.writeText(resetLink);
                      toast({
                        title: 'Enlace copiado',
                        description: 'El enlace ha sido copiado al portapapeles',
                        status: 'success',
                        duration: 2000,
                      });
                    }}
                  >
                    Copiar Enlace
                  </Button>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/*<Card bg={bgColor}>
          <CardBody>
            <VStack spacing={4}>
              <Heading size="md">Estadísticas</Heading>
              <HStack justify="space-between" width="full">
                <Text>Pronósticos realizados:</Text>
                <Text fontWeight="bold">0</Text>
              </HStack>
              <HStack justify="space-between" width="full">
                <Text>Puntos totales:</Text>
                <Text fontWeight="bold">0</Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>*/}
      </VStack>
    </Container>
  );
};

export default ProfilePage; 